import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      if (cancelled) return;
      tries += 1;
      setAttempts(tries);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed"); return;
        }
      } catch { /* keep polling */ }
      if (tries >= 15) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen grid place-items-center bg-background p-8">
      <div className="max-w-md w-full border border-border bg-card p-10 rounded-sm text-center" data-testid="payment-success-page">
        {status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin" />
            <h1 className="font-display text-2xl font-bold mt-6">Confirming payment…</h1>
            <p className="text-sm text-muted-foreground mt-2 font-mono-plex">
              Attempt {attempts}/15
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" data-testid="payment-paid" />
            <h1 className="font-display text-3xl font-bold mt-6">Welcome to Business.</h1>
            <p className="text-sm text-muted-foreground mt-3">
              Your workspace is now on the Business plan. All modules and AI features unlocked.
            </p>
            <Button onClick={() => nav("/app")} className="mt-8 rounded-sm bg-primary hover:bg-primary/90 w-full h-11" data-testid="go-to-dashboard">
              Go to Dashboard
            </Button>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-destructive" />
            <h1 className="font-display text-2xl font-bold mt-6">
              {status === "failed" ? "Payment failed" : "Still processing"}
            </h1>
            <p className="text-sm text-muted-foreground mt-3">
              {status === "failed"
                ? "The payment could not be completed. No charges made."
                : "Confirmation is taking longer than expected. Check your dashboard shortly."}
            </p>
            <Button onClick={() => nav("/")} variant="outline" className="mt-8 rounded-sm w-full h-11">
              Back to home
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
